/* Huemna custom print studio v4
 * Modes:
 *  - single:  every uploaded image becomes its own glass print (own size, orientation, crop) -> one cart line each.
 *  - collage: 2-12 images composed on one glass -> rendered client-side into ONE final image -> one cart line.
 * The customer never sees resolution warnings; resolution is recorded in hidden properties for the studio,
 * which enhances/upscales every file before printing.
 */
(() => {
  if (customElements.get('custom-print-studio')) return;

  const MAX_ZOOM = 6;
  const MAX_CANVAS_PIXELS = 16000000; // stays under iOS Safari canvas limit (16.7 MP)
  const TARGET_PPI = 300; // collage output cap; studio upscales further

  // Geometry: fit image (iw x ih) into cell (W x H) with "cover" * zoom, centred at (cx, cy) of the image.
  function place(img, W, H) {
    const base = Math.max(W / img.width, H / img.height);
    const s = base * img.zoom;
    const dw = img.width * s;
    const dh = img.height * s;
    let ox = W / 2 - img.cx * dw;
    let oy = H / 2 - img.cy * dh;
    ox = Math.min(0, Math.max(W - dw, ox));
    oy = Math.min(0, Math.max(H - dh, oy));
    return { s, dw, dh, ox, oy };
  }
  function commit(img, W, H, p) {
    img.cx = (W / 2 - p.ox) / p.dw;
    img.cy = (H / 2 - p.oy) / p.dh;
  }
  function sourceRect(img, W, H) {
    const p = place(img, W, H);
    return { sx: -p.ox / p.s, sy: -p.oy / p.s, sw: W / p.s, sh: H / p.s };
  }

  customElements.define(
    'custom-print-studio',
    class CustomPrintStudio extends HTMLElement {
      connectedCallback() {
        this.ac?.abort();
        this.ac = new AbortController();
        const signal = this.ac.signal;
        this.mode = this.dataset.mode === 'collage' ? 'collage' : 'single';
        this.stageIndex = 0;
        this.images = [];
        this.active = 0;
        this.layoutId = 'balanced';
        this.spacing = 'thin';
        this.collageVariant = null;
        this.collageOrientation = 'portrait';
        this.maxFiles = Number(this.dataset.maxFiles || 12);
        this.maxFileBytes = Number(this.dataset.maxFileBytes || 20971520);
        this.maxMegapixels = Number(this.dataset.maxMegapixels || 50);
        this.preferredPpi = Number(this.dataset.preferredPpi || 150);
        this.minimumPpi = Number(this.dataset.minimumPpi || 120);

        const $ = (s) => this.querySelector(s);
        this.el = {
          file: $('[data-file-input]'), dropzone: $('[data-dropzone]'), dropTitle: $('[data-dropzone-title]'),
          fileResult: $('[data-file-result]'), fileName: $('[data-file-name]'), fileMeta: $('[data-file-meta]'), fileList: $('[data-file-list]'),
          layoutChooser: $('[data-layout-chooser]'), manyNote: $('[data-many-note]'),
          stages: [...this.querySelectorAll('[data-stage]')], progress: [...this.querySelectorAll('[data-progress-item]')],
          variants: [...this.querySelectorAll('[data-variant-option]')], orient: [...this.querySelectorAll('[data-orientation-option]')],
          spacing: [...this.querySelectorAll('[data-spacing-option]')], layouts: [...this.querySelectorAll('[data-layout-option]')],
          applyAll: $('[data-apply-all]'), tabsSize: $('[data-print-tabs="size"]'), tabsCrop: $('[data-print-tabs="crop"]'),
          makePrimary: $('[data-make-primary]'),
          frame: $('[data-preview-frame]'), canvas: $('[data-preview-canvas]'), empty: $('[data-preview-empty]'), hint: $('[data-gesture-hint]'),
          previewSize: $('[data-preview-size]'), nav: $('[data-preview-nav]'), counter: $('[data-preview-counter]'),
          zoom: $('[data-zoom-controls]'), zoomRange: $('[data-zoom-range]'),
          approval: $('[data-crop-approval]'), rights: $('[data-rights-confirmation]'),
          primary: $('[data-primary-action]'), back: $('[data-back]'), price: $('[data-summary-price]'), priceLabel: $('[data-summary-label]'),
        };
        const e = this.el;
        e.file.addEventListener('change', () => this.addFiles(e.file.files), { signal });
        $('[data-add-files]')?.addEventListener('click', () => e.file.click(), { signal });
        $('[data-clear-files]')?.addEventListener('click', () => this.clearAll(), { signal });
        ['dragenter', 'dragover'].forEach((n) => e.dropzone.addEventListener(n, (ev) => { ev.preventDefault(); e.dropzone.dataset.dragging = 'true'; }, { signal }));
        ['dragleave', 'drop'].forEach((n) => e.dropzone.addEventListener(n, () => delete e.dropzone.dataset.dragging, { signal }));
        e.dropzone.addEventListener('drop', (ev) => { ev.preventDefault(); this.addFiles(ev.dataTransfer.files); }, { signal });
        e.variants.forEach((o) => o.addEventListener('change', () => this.setVariant(o.value), { signal }));
        e.orient.forEach((o) => o.addEventListener('change', () => this.setOrientation(o.value), { signal }));
        e.spacing.forEach((o) => o.addEventListener('change', () => { this.spacing = o.value; this.render(); }, { signal }));
        e.layouts.forEach((o) => o.addEventListener('change', () => { this.layoutId = o.value; this.resetApproval(); this.render(); }, { signal }));
        e.applyAll?.addEventListener('click', () => this.applySizeToAll(), { signal });
        e.makePrimary?.addEventListener('click', () => this.makeLead(), { signal });
        $('[data-prev-print]')?.addEventListener('click', () => this.select(this.active - 1), { signal });
        $('[data-next-print]')?.addEventListener('click', () => this.select(this.active + 1), { signal });
        $('[data-zoom-in]')?.addEventListener('click', () => this.zoomBy(1.2), { signal });
        $('[data-zoom-out]')?.addEventListener('click', () => this.zoomBy(1 / 1.2), { signal });
        $('[data-zoom-reset]')?.addEventListener('click', () => this.resetCrop(), { signal });
        e.zoomRange?.addEventListener('input', () => this.setZoom(Number(e.zoomRange.value) / 100), { signal });
        e.approval.addEventListener('change', () => this.updatePrimary(), { signal });
        e.rights.addEventListener('change', () => this.updatePrimary(), { signal });
        e.primary.addEventListener('click', () => this.advance(), { signal });
        e.back.addEventListener('click', () => this.goTo(this.stageIndex - 1), { signal });
        this.bindGestures(signal);
        window.addEventListener('pageshow', () => { this.busy = false; this.updatePrimary(); }, { signal });
        this.defaultVariant = (e.variants.find((o) => o.checked && !o.disabled) || e.variants.find((o) => !o.disabled))?.value || null;
        this.goTo(0, false);
      }

      disconnectedCallback() {
        this.ac?.abort();
        this.images.forEach((i) => URL.revokeObjectURL(i.previewUrl));
      }

      /* ---------- helpers ---------- */
      t(key, n) { return (this.dataset[key] || '').replace('%n', n ?? ''); }
      dispatch(name, detail = {}) { window.dispatchEvent(new CustomEvent(`huemna:${name}`, { detail })); }
      variantEl(id) { return this.el.variants.find((o) => o.value === String(id)); }
      money(cents) {
        const cur = window.Shopify?.currency?.active || 'EUR';
        try { return new Intl.NumberFormat(document.documentElement.lang || undefined, { style: 'currency', currency: cur }).format(cents / 100); }
        catch { return (cents / 100).toFixed(2) + ' ' + cur; }
      }
      // The "print" currently being edited: in single mode an image, in collage mode the shared settings.
      printOf(i = this.active) {
        if (this.mode === 'collage') return { variantId: this.collageVariant, orientation: this.collageOrientation };
        return this.images[i];
      }
      dims(print) {
        const v = this.variantEl(print?.variantId);
        let w = Number(v?.dataset.widthCm || 0), h = Number(v?.dataset.heightCm || 0);
        if (!(w > 0 && h > 0)) { w = 7; h = 11; }
        const portrait = Math.min(w, h), long = Math.max(w, h);
        return print?.orientation === 'landscape' ? { w: long, h: portrait } : { w: portrait, h: long };
      }
      ratio(print) { const d = this.dims(print); return d.w / d.h; }
      fileKey(f) { return `${f.name}:${f.size}:${f.lastModified}`; }
      formatBytes(b) { return b < 1048576 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1048576).toFixed(1)} MB`; }
      setMessage(i, msg) { const m = this.querySelector(`[data-stage-message="${i}"]`); if (m) { m.textContent = msg || ''; m.hidden = !msg; } }

      /* ---------- files ---------- */
      async addFiles(list) {
        if (this.busy) return;
        const files = [...(list || [])];
        if (!files.length) return;
        this.busy = true;
        this.setMessage(0, '');
        this.el.dropTitle.textContent = this.dataset.checkingLabel;
        const errors = [];
        const keys = new Set(this.images.map((i) => this.fileKey(i.file)));
        let total = this.images.reduce((s, i) => s + i.file.size, 0);
        if (files.length + this.images.length > this.maxFiles) errors.push(this.dataset.fileLimitError);
        const ok = new Set(['image/jpeg', 'image/png', 'image/webp']);
        for (const file of files) {
          if (this.images.length >= this.maxFiles) break;
          if (keys.has(this.fileKey(file))) continue;
          if ((file.type && !ok.has(file.type)) || (!file.type && !/\.(jpe?g|png|webp)$/i.test(file.name))) { errors.push(this.dataset.fileTypeError); continue; }
          if (file.size > this.maxFileBytes) { errors.push(this.dataset.fileSizeError); continue; }
          if (total + file.size > 40 * 1048576) { errors.push(this.dataset.totalSizeError); continue; }
          try {
            const d = await this.decodePreview(file);
            if ((d.width * d.height) / 1e6 > this.maxMegapixels) { URL.revokeObjectURL(d.previewUrl); errors.push(this.dataset.filePixelsError); continue; }
            keys.add(this.fileKey(file));
            total += file.size;
            this.images.push({
              file, width: d.width, height: d.height, previewUrl: d.previewUrl,
              zoom: 1, cx: 0.5, cy: 0.5,
              variantId: this.defaultVariant,
              orientation: this.el.orient.length && d.width > d.height * 1.05 ? 'landscape' : 'portrait',
            });
          } catch { errors.push(this.dataset.fileReadError); }
        }
        this.busy = false;
        this.el.file.value = '';
        this.el.dropTitle.textContent = this.dataset.chooseLabel;
        if (this.mode === 'collage' && !this.collageVariant) {
          this.collageVariant = this.defaultVariant;
          const first = this.images[0];
          if (first) this.collageOrientation = first.orientation;
        }
        if (errors.length) this.setMessage(0, [...new Set(errors)].join(' '));
        this.afterImagesChanged();
        if (this.images.length) this.dispatch('custom_print_file_check', { product_id: this.dataset.productId, mode: this.mode, file_count: this.images.length });
      }

      decodePreview(file) {
        return new Promise((resolve, reject) => {
          const img = new Image();
          const url = URL.createObjectURL(file);
          img.decoding = 'async';
          img.onload = () => {
            const width = img.naturalWidth, height = img.naturalHeight;
            const k = Math.min(1, 1600 / Math.max(width, height));
            const c = document.createElement('canvas');
            c.width = Math.max(1, Math.round(width * k));
            c.height = Math.max(1, Math.round(height * k));
            const ctx = c.getContext('2d');
            if (!ctx) { URL.revokeObjectURL(url); reject(new Error('canvas')); return; }
            ctx.drawImage(img, 0, 0, c.width, c.height);
            c.toBlob((b) => { URL.revokeObjectURL(url); b ? resolve({ width, height, previewUrl: URL.createObjectURL(b) }) : reject(new Error('encode')); }, 'image/jpeg', 0.86);
          };
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
          img.src = url;
        });
      }

      removeImage(i) {
        const [r] = this.images.splice(i, 1);
        if (r) URL.revokeObjectURL(r.previewUrl);
        this.active = Math.max(0, Math.min(this.active, this.images.length - 1));
        this.afterImagesChanged();
      }

      clearAll() {
        this.images.forEach((i) => URL.revokeObjectURL(i.previewUrl));
        this.images = [];
        this.active = 0;
        this.collageVariant = null;
        this.setMessage(0, '');
        this.afterImagesChanged();
        this.goTo(0, false);
      }

      afterImagesChanged() {
        const e = this.el, n = this.images.length;
        e.dropzone.hidden = n > 0;
        e.fileResult.hidden = n === 0;
        if (e.layoutChooser) e.layoutChooser.hidden = n < 2;
        if (e.manyNote) e.manyNote.hidden = n < 2;
        const bytes = this.images.reduce((s, i) => s + i.file.size, 0);
        e.fileName.textContent = n === 1 ? this.images[0].file.name
          : this.mode === 'collage' ? this.t('imagesCountLabel', n) : this.t('printsCountLabel', n);
        e.fileMeta.textContent = n ? `${this.formatBytes(bytes)} · ${n} / ${this.maxFiles}` : '';
        e.fileList.replaceChildren(...this.images.map((img, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'custom-print__file-thumb';
          b.setAttribute('aria-label', `${this.dataset.removeImageLabel} ${i + 1}`);
          const im = document.createElement('img');
          im.src = img.previewUrl; im.alt = ''; im.width = 68; im.height = 68;
          const x = document.createElement('span');
          x.className = 'custom-print__file-remove';
          x.setAttribute('aria-hidden', 'true');
          x.textContent = '×';
          b.append(im, x);
          b.addEventListener('click', () => this.removeImage(i));
          return b;
        }));
        this.resetApproval();
        this.render();
      }

      /* ---------- choices ---------- */
      select(i) {
        const count = this.mode === 'collage' ? this.images.length : this.images.length;
        if (!count) return;
        this.active = (i + count) % count;
        this.render();
      }
      setVariant(id) {
        if (this.mode === 'collage') this.collageVariant = id;
        else if (this.images[this.active]) this.images[this.active].variantId = id;
        this.resetApproval();
        this.render();
        const v = this.variantEl(id);
        this.dispatch('custom_print_size_select', { product_id: this.dataset.productId, variant_id: id, size: v?.dataset.sizeLabel });
      }
      setOrientation(o) {
        if (this.mode === 'collage') this.collageOrientation = o;
        else if (this.images[this.active]) this.images[this.active].orientation = o;
        this.resetApproval();
        this.render();
      }
      applySizeToAll() {
        const cur = this.images[this.active];
        if (!cur) return;
        this.images.forEach((i) => { i.variantId = cur.variantId; });
        this.render();
        if (this.el.applyAll) { this.el.applyAll.textContent = this.dataset.applyAllDone; setTimeout(() => { this.el.applyAll.textContent = this.el.applyAll.dataset.label; }, 1600); }
      }
      makeLead() {
        if (this.active <= 0) return;
        const [x] = this.images.splice(this.active, 1);
        this.images.unshift(x);
        this.active = 0;
        this.resetApproval();
        this.render();
      }

      /* ---------- collage layout ---------- */
      layoutRects(count, R) {
        if (this.mode !== 'collage' || count <= 1) return [{ x: 0, y: 0, width: 1, height: 1 }];
        const imgs = this.images;
        // Pick the grid (columns) that keeps the most of every photo for the chosen glass shape.
        const bestGrid = (list, ox, oy, aw, ah) => {
          let best = null;
          for (let cols = 1; cols <= list.length; cols += 1) {
            const rects = this.grid(list.length, cols, ox, oy, aw, ah);
            const score = rects.reduce((s, r, k) => {
              const cellR = (r.width * R) / r.height, imR = list[k].width / list[k].height;
              return s + Math.min(cellR, imR) / Math.max(cellR, imR);
            }, 0);
            if (!best || score > best.score + 1e-6) best = { score, rects };
          }
          return best.rects;
        };
        if (this.layoutId === 'balanced') return bestGrid(imgs, 0, 0, 1, 1);
        const lead = 0.618;
        if (this.layoutId === 'editorial') {
          const rest = imgs.slice(1);
          if (R < 0.9) return [{ x: 0, y: 0, width: 1, height: lead }, ...bestGrid(rest, 0, lead, 1, 1 - lead)];
          return [{ x: 0, y: 0, width: lead, height: 1 }, ...bestGrid(rest, lead, 0, 1 - lead, 1)];
        }
        // rhythm: alternating wide / narrow pairs
        const rects = [];
        if (R < 1) {
          const rows = Math.ceil(count / 2), rh = 1 / rows;
          let k = 0;
          for (let r = 0; r < rows; r += 1) {
            if (count - k === 1) { rects.push({ x: 0, y: r * rh, width: 1, height: rh }); break; }
            const wide = r % 2 === 0 ? lead : 1 - lead;
            rects.push({ x: 0, y: r * rh, width: wide, height: rh }, { x: wide, y: r * rh, width: 1 - wide, height: rh });
            k += 2;
          }
        } else {
          const cols = Math.ceil(count / 2), cw = 1 / cols;
          let k = 0;
          for (let c = 0; c < cols; c += 1) {
            if (count - k === 1) { rects.push({ x: c * cw, y: 0, width: cw, height: 1 }); break; }
            const tall = c % 2 === 0 ? lead : 1 - lead;
            rects.push({ x: c * cw, y: 0, width: cw, height: tall }, { x: c * cw, y: tall, width: cw, height: 1 - tall });
            k += 2;
          }
        }
        return rects;
      }
      grid(count, cols, ox = 0, oy = 0, aw = 1, ah = 1) {
        const rows = Math.ceil(count / cols), rh = ah / rows, out = [];
        let k = 0;
        for (let r = 0; r < rows; r += 1) {
          const inRow = Math.min(cols, count - k), cw = aw / inRow;
          for (let c = 0; c < inRow; c += 1) { out.push({ x: ox + c * cw, y: oy + r * rh, width: cw, height: rh }); k += 1; }
        }
        return out;
      }
      gap() { return this.mode === 'collage' && this.spacing === 'thin' && this.images.length > 1 ? 0.012 : 0; } // fraction of the short side

      // Cells of the print being shown, in "frame units" (frame height = 1, width = R), with gaps applied.
      cells() {
        const print = this.printOf();
        const R = this.ratio(print);
        if (this.mode === 'single') return [{ index: this.active, x: 0, y: 0, W: R, H: 1 }];
        const g = this.gap() * Math.min(R, 1);
        return this.layoutRects(this.images.length, R).map((r, index) => {
          const x = r.x * R, y = r.y, W = r.width * R, H = r.height;
          const gl = r.x > 1e-6 ? g / 2 : 0, gr = r.x + r.width < 1 - 1e-6 ? g / 2 : 0;
          const gt = r.y > 1e-6 ? g / 2 : 0, gb = r.y + r.height < 1 - 1e-6 ? g / 2 : 0;
          return { index, x: x + gl, y: y + gt, W: W - gl - gr, H: H - gt - gb };
        });
      }

      /* ---------- rendering ---------- */
      render() {
        const e = this.el, n = this.images.length;
        if (n === 0) {
          e.canvas.hidden = true; e.empty.hidden = false; e.nav.hidden = true; e.zoom.hidden = true; e.hint.hidden = true;
          e.previewSize.textContent = '';
          this.updateSummary();
          this.updatePrimary();
          return;
        }
        if (this.active >= n) this.active = n - 1;
        const print = this.printOf();
        const R = this.ratio(print);
        e.frame.style.setProperty('--print-ratio', String(R));
        e.canvas.hidden = false; e.empty.hidden = true;
        const cells = this.cells();
        this.currentCells = cells;
        e.canvas.replaceChildren(...cells.map((c) => {
          const img = this.images[c.index];
          const cell = document.createElement('div');
          cell.className = 'custom-print__preview-cell';
          cell.dataset.cell = String(c.index);
          cell.dataset.active = String(this.mode === 'collage' && c.index === this.active && n > 1);
          cell.style.left = `${(c.x / R) * 100}%`;
          cell.style.top = `${c.y * 100}%`;
          cell.style.width = `${(c.W / R) * 100}%`;
          cell.style.height = `${c.H * 100}%`;
          const im = document.createElement('img');
          im.src = img.previewUrl;
          im.alt = `${this.dataset.previewAlt} ${c.index + 1}`;
          im.draggable = false;
          cell.append(im);
          this.positionImage(cell, c);
          return cell;
        }));
        // label + nav
        const v = this.variantEl(print.variantId);
        const orientLabel = print.orientation === 'landscape' ? ' · ↔' : '';
        e.previewSize.textContent = v ? `${v.closest('.custom-print__size-option')?.querySelector('.custom-print__size-title')?.textContent.trim() || v.dataset.sizeLabel}${orientLabel} · ${v.dataset.price}` : '';
        e.nav.hidden = this.mode !== 'single' || n < 2;
        e.counter.textContent = `${this.dataset.printLabel} ${this.active + 1} / ${n}`;
        const cropStage = this.stageIndex === 2;
        e.zoom.hidden = !cropStage;
        e.hint.hidden = !cropStage || this.hintSeen;
        this.syncZoomRange();
        // controls reflect the active print
        e.variants.forEach((o) => { o.checked = o.value === String(print.variantId); });
        e.orient.forEach((o) => { o.checked = o.value === print.orientation; });
        e.spacing.forEach((o) => { o.checked = o.value === this.spacing; });
        if (e.applyAll) { e.applyAll.dataset.label ||= e.applyAll.textContent; e.applyAll.hidden = this.mode !== 'single' || n < 2; }
        if (e.makePrimary) e.makePrimary.hidden = !(cropStage && n > 1 && this.active > 0 && this.layoutId !== 'balanced');
        this.renderTabs(e.tabsSize, this.mode === 'single' && n > 1);
        this.renderTabs(e.tabsCrop, n > 1);
        this.updateSummary();
        this.updatePrimary();
      }

      renderTabs(host, show) {
        if (!host) return;
        host.hidden = !show;
        if (!show) return;
        host.replaceChildren(...this.images.map((img, i) => {
          const b = document.createElement('button');
          b.type = 'button';
          b.className = 'custom-print__print-tab';
          b.setAttribute('aria-pressed', String(i === this.active));
          const im = document.createElement('img');
          im.src = img.previewUrl; im.alt = ''; im.width = 56; im.height = 56;
          const s = document.createElement('span');
          const v = this.mode === 'single' ? this.variantEl(img.variantId) : null;
          s.textContent = `${this.mode === 'single' ? this.dataset.printLabel : this.dataset.imageLabel} ${i + 1}${v ? ' · ' + (v.dataset.sizeLabel.split('—')[0] || '').trim() : ''}`;
          b.append(im, s);
          b.addEventListener('click', () => this.select(i));
          return b;
        }));
      }

      positionImage(cell, c) {
        const img = this.images[c.index];
        const p = place(img, c.W, c.H);
        commit(img, c.W, c.H, p);
        const im = cell.querySelector('img');
        im.style.width = `${(p.dw / c.W) * 100}%`;
        im.style.height = `${(p.dh / c.H) * 100}%`;
        im.style.left = `${(p.ox / c.W) * 100}%`;
        im.style.top = `${(p.oy / c.H) * 100}%`;
      }
      refreshCell(index) {
        const c = this.currentCells?.find((x) => x.index === index);
        const cell = this.el.canvas.querySelector(`[data-cell="${index}"]`);
        if (c && cell) this.positionImage(cell, c);
        this.syncZoomRange();
      }
      syncZoomRange() {
        const img = this.images[this.active];
        if (img && this.el.zoomRange) this.el.zoomRange.value = String(Math.round(img.zoom * 100));
      }

      /* ---------- crop interaction ---------- */
      zoomAt(index, factor, ax, ay) {
        const img = this.images[index];
        const c = this.currentCells?.find((x) => x.index === index);
        if (!img || !c) return;
        const p = place(img, c.W, c.H);
        const px = (ax - p.ox) / p.s, py = (ay - p.oy) / p.s; // anchor in image pixels
        img.zoom = Math.min(MAX_ZOOM, Math.max(1, img.zoom * factor));
        const base = Math.max(c.W / img.width, c.H / img.height), s2 = base * img.zoom;
        const dw = img.width * s2, dh = img.height * s2;
        let ox = ax - px * s2, oy = ay - py * s2;
        ox = Math.min(0, Math.max(c.W - dw, ox));
        oy = Math.min(0, Math.max(c.H - dh, oy));
        commit(img, c.W, c.H, { dw, dh, ox, oy });
        this.touched();
        this.refreshCell(index);
      }
      zoomBy(f) { const c = this.currentCells?.find((x) => x.index === this.active); if (c) this.zoomAt(this.active, f, c.W / 2, c.H / 2); }
      setZoom(z) { const img = this.images[this.active]; if (img) this.zoomBy(z / img.zoom); }
      resetCrop() { const img = this.images[this.active]; if (!img) return; img.zoom = 1; img.cx = 0.5; img.cy = 0.5; this.touched(); this.refreshCell(this.active); }
      pan(index, dxUnits, dyUnits) {
        const img = this.images[index];
        const c = this.currentCells?.find((x) => x.index === index);
        if (!img || !c) return;
        const p = place(img, c.W, c.H);
        p.ox = Math.min(0, Math.max(c.W - p.dw, p.ox + dxUnits));
        p.oy = Math.min(0, Math.max(c.H - p.dh, p.oy + dyUnits));
        commit(img, c.W, c.H, p);
        this.refreshCell(index);
      }
      touched() {
        this.resetApproval();
        if (!this.hintSeen) { this.hintSeen = true; this.el.hint.hidden = true; }
      }

      bindGestures(signal) {
        const canvas = this.el.canvas;
        const pointers = new Map();
        let target = null, last = null;
        const unitsPerPx = (cellEl, c) => c.W / cellEl.getBoundingClientRect().width;
        const cellInfo = (ev) => {
          const cellEl = ev.target.closest?.('[data-cell]');
          if (!cellEl) return null;
          const index = Number(cellEl.dataset.cell);
          return { cellEl, index, c: this.currentCells.find((x) => x.index === index) };
        };
        const local = (info, x, y) => {
          const r = info.cellEl.getBoundingClientRect();
          const k = info.c.W / r.width;
          return { x: (x - r.left) * k, y: (y - r.top) * k };
        };
        canvas.addEventListener('pointerdown', (ev) => {
          const info = cellInfo(ev);
          if (!info) return;
          if (this.mode === 'collage' && info.index !== this.active && pointers.size === 0) {
            this.active = info.index;
            this.el.canvas.querySelectorAll('[data-cell]').forEach((el) => { el.dataset.active = String(Number(el.dataset.cell) === this.active); });
            this.renderTabs(this.el.tabsCrop, this.images.length > 1);
            this.syncZoomRange();
            if (this.el.makePrimary) this.el.makePrimary.hidden = !(this.stageIndex === 2 && this.images.length > 1 && this.active > 0 && this.layoutId !== 'balanced');
          }
          if (this.stageIndex !== 2) return;
          ev.preventDefault();
          canvas.setPointerCapture?.(ev.pointerId);
          pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
          target = target || info;
          last = null;
        }, { signal });
        canvas.addEventListener('pointermove', (ev) => {
          if (!pointers.has(ev.pointerId) || !target) return;
          ev.preventDefault();
          const prev = pointers.get(ev.pointerId);
          pointers.set(ev.pointerId, { x: ev.clientX, y: ev.clientY });
          if (pointers.size === 1) {
            const k = unitsPerPx(target.cellEl, target.c);
            this.pan(target.index, (ev.clientX - prev.x) * k, (ev.clientY - prev.y) * k);
            this.touched();
          } else if (pointers.size >= 2) {
            const [a, b] = [...pointers.values()];
            const dist = Math.hypot(a.x - b.x, a.y - b.y);
            const mid = local(target, (a.x + b.x) / 2, (a.y + b.y) / 2);
            if (last) {
              const k = unitsPerPx(target.cellEl, target.c);
              this.pan(target.index, (mid.x - last.mid.x), (mid.y - last.mid.y));
              if (last.dist > 0) this.zoomAt(target.index, dist / last.dist, mid.x, mid.y);
              void k;
            }
            last = { dist, mid };
          }
        }, { signal });
        const end = (ev) => {
          pointers.delete(ev.pointerId);
          last = null;
          if (pointers.size === 0) target = null;
        };
        canvas.addEventListener('pointerup', end, { signal });
        canvas.addEventListener('pointercancel', end, { signal });
        canvas.addEventListener('wheel', (ev) => {
          if (this.stageIndex !== 2) return;
          const info = cellInfo(ev);
          if (!info) return;
          ev.preventDefault();
          const p = local(info, ev.clientX, ev.clientY);
          this.zoomAt(info.index, Math.exp(-ev.deltaY * 0.0015), p.x, p.y);
        }, { passive: false, signal });
        canvas.addEventListener('keydown', (ev) => {
          if (this.stageIndex !== 2) return;
          const step = 0.02;
          const c = this.currentCells?.find((x) => x.index === this.active);
          if (!c) return;
          const map = { ArrowLeft: [step, 0], ArrowRight: [-step, 0], ArrowUp: [0, step], ArrowDown: [0, -step] };
          if (map[ev.key]) { ev.preventDefault(); this.pan(this.active, map[ev.key][0] * c.W, map[ev.key][1] * c.H); this.touched(); }
          if (ev.key === '+' || ev.key === '=') { ev.preventDefault(); this.zoomBy(1.1); }
          if (ev.key === '-') { ev.preventDefault(); this.zoomBy(1 / 1.1); }
        }, { signal });
      }

      /* ---------- summary / navigation ---------- */
      lines() {
        if (this.mode === 'collage') return this.images.length >= 2 && this.collageVariant ? [{ variantId: this.collageVariant }] : [];
        return this.images.map((i) => ({ variantId: i.variantId }));
      }
      updateSummary() {
        const lines = this.lines().filter((l) => this.variantEl(l.variantId));
        const cents = lines.reduce((s, l) => s + Number(this.variantEl(l.variantId).dataset.priceCents || 0), 0);
        if (lines.length) this.el.price.textContent = this.money(cents);
        if (this.el.priceLabel) this.el.priceLabel.textContent = this.mode === 'single' && lines.length > 1 ? this.t('printsCountLabel', lines.length) : this.el.priceLabel.dataset.orig || (this.el.priceLabel.dataset.orig = this.el.priceLabel.textContent);
        this.el.primary.textContent = this.stageIndex < 2 ? this.dataset.continueLabel
          : this.mode === 'collage' ? this.dataset.addLabelCollage
            : lines.length > 1 ? this.t('addLabelMany', lines.length) : this.dataset.addLabelSingle;
      }
      resetApproval() { if (this.el.approval) this.el.approval.checked = false; this.updatePrimary(); }
      ready() {
        if (!this.images.length) return false;
        if (this.mode === 'collage' && this.images.length < 2) return false;
        return this.lines().every((l) => { const v = this.variantEl(l.variantId); return v && !v.disabled; });
      }
      updatePrimary() {
        const e = this.el;
        if (!e?.primary) return;
        if (this.busy) { e.primary.disabled = true; return; }
        if (this.stageIndex === 0) e.primary.disabled = !this.images.length;
        else if (this.stageIndex === 1) e.primary.disabled = !this.ready();
        else e.primary.disabled = !(this.ready() && e.approval.checked && e.rights.checked);
      }
      advance() {
        this.setMessage(this.stageIndex, '');
        if (this.stageIndex === 0) {
          if (this.mode === 'collage' && this.images.length < 2) { this.setMessage(0, this.dataset.collageMinError); return; }
          this.goTo(1); return;
        }
        if (this.stageIndex === 1) { if (!this.ready()) { this.setMessage(1, this.dataset.noSizeError); return; } this.goTo(2); return; }
        if (!this.el.approval.checked || !this.el.rights.checked) { this.setMessage(2, this.dataset.requiredError); return; }
        this.submit();
      }
      goTo(i, focus = true) {
        this.stageIndex = Math.max(0, Math.min(i, 2));
        this.dataset.currentStage = ['upload', 'size', 'crop'][this.stageIndex];
        this.el.stages.forEach((s, k) => { s.hidden = k !== this.stageIndex; });
        this.el.progress.forEach((p, k) => { if (k === this.stageIndex) p.setAttribute('aria-current', 'step'); else p.removeAttribute('aria-current'); p.dataset.complete = String(k < this.stageIndex); });
        this.el.back.hidden = this.stageIndex === 0;
        this.el.canvas.tabIndex = this.stageIndex === 2 ? 0 : -1;
        this.render();
        if (focus) this.el.stages[this.stageIndex]?.querySelector('h2')?.focus({ preventScroll: true });
        if (focus && this.stageIndex > 0) {
          const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
          if (window.matchMedia('(max-width: 989px)').matches) this.el.frame.scrollIntoView({ behavior, block: 'center' });
          else {
            const layout = this.querySelector('.custom-print__layout');
            const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--sticky-header-height')) || 80;
            const top = layout.getBoundingClientRect().top + window.scrollY - header - 16;
            if (Math.abs(window.scrollY - top) > 40) window.scrollTo({ top, behavior });
          }
        }
      }

      /* ---------- output ---------- */
      loadFull(file) {
        return new Promise((resolve, reject) => {
          const url = URL.createObjectURL(file);
          const img = new Image();
          img.decoding = 'async';
          img.onload = () => resolve({ img, url });
          img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('decode')); };
          img.src = url;
        });
      }
      toBlob(canvas) { return new Promise((res, rej) => canvas.toBlob((b) => (b ? res(b) : rej(new Error('encode'))), 'image/jpeg', 0.95)); }

      // Single print: crop of the original at its native resolution (never enlarged).
      async renderSingle(img) {
        const d = this.dims(img);
        const R = d.w / d.h;
        const src = sourceRect(img, R, 1);
        let w = Math.round(src.sw), h = Math.round(src.sh);
        const k = Math.min(1, Math.sqrt(MAX_CANVAS_PIXELS / (w * h)));
        w = Math.max(1, Math.round(w * k)); h = Math.max(1, Math.round(h * k));
        const { img: full, url } = await this.loadFull(img.file);
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        const ctx = c.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, w, h);
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(full, src.sx, src.sy, src.sw, src.sh, 0, 0, w, h);
        URL.revokeObjectURL(url);
        const blob = await this.toBlob(c);
        const ppi = Math.floor(Math.min(src.sw / (d.w / 2.54), src.sh / (d.h / 2.54)));
        return { blob, w, h, ppi, crop: { x: Math.round(src.sx), y: Math.round(src.sy), w: Math.round(src.sw), h: Math.round(src.sh), zoom: +img.zoom.toFixed(3) } };
      }

      // Collage: one finished image. Resolution follows the sharpest source, capped at 150 PPI and 16 MP.
      async renderCollage() {
        const print = this.printOf();
        const d = this.dims(print);
        const R = d.w / d.h;
        const cells = this.cells();
        let pxPerUnit = 0; // pixels per frame-height unit
        const cellPpi = [];
        cells.forEach((c) => {
          const src = sourceRect(this.images[c.index], c.W, c.H);
          const native = src.sh / c.H;
          pxPerUnit = Math.max(pxPerUnit, native);
          cellPpi.push(Math.floor(src.sh / ((c.H * d.h) / 2.54)));
        });
        pxPerUnit = Math.min(pxPerUnit, (TARGET_PPI * d.h) / 2.54);
        let H = pxPerUnit, W = pxPerUnit * R;
        const k = Math.min(1, Math.sqrt(MAX_CANVAS_PIXELS / (W * H)));
        W = Math.max(1, Math.round(W * k)); H = Math.max(1, Math.round(H * k));
        const scale = H; // frame units -> px
        const c2 = document.createElement('canvas');
        c2.width = W; c2.height = H;
        const ctx = c2.getContext('2d');
        ctx.fillStyle = '#fff'; ctx.fillRect(0, 0, W, H);
        ctx.imageSmoothingQuality = 'high';
        for (const c of cells) {
          const img = this.images[c.index];
          const src = sourceRect(img, c.W, c.H);
          const { img: full, url } = await this.loadFull(img.file);
          const dx = Math.round(c.x * scale), dy = Math.round(c.y * scale);
          const dw = Math.round((c.x + c.W) * scale) - dx, dh = Math.round((c.y + c.H) * scale) - dy;
          ctx.drawImage(full, src.sx, src.sy, src.sw, src.sh, dx, dy, dw, dh);
          URL.revokeObjectURL(url);
        }
        const blob = await this.toBlob(c2);
        return { blob, w: W, h: H, ppi: Math.min(...cellPpi), cellPpi, cells };
      }

      quality(ppi) { return ppi >= this.preferredPpi ? 'ready' : ppi >= this.minimumPpi ? 'review' : 'studio_upscale'; }

      async submit() {
        if (this.busy) return;
        this.busy = true;
        const e = this.el;
        e.primary.disabled = true;
        e.primary.textContent = this.dataset.preparingLabel;
        const base = (fd) => {
          fd.append('quantity', '1');
          fd.append('properties[_custom_print_schema]', 'huemna-custom-print-v4');
          fd.append('properties[_mode]', this.mode);
          fd.append('properties[_preview_approved]', 'yes');
          fd.append('properties[_rights_confirmed]', 'yes');
        };
        try {
          const forms = [];
          if (this.mode === 'collage') {
            const out = await this.renderCollage();
            const d = this.dims(this.printOf());
            const fd = new FormData();
            fd.append('id', this.collageVariant);
            base(fd);
            fd.append('properties[Print file]', out.blob, 'huemna-collage.jpg');
            fd.append('properties[_orientation]', this.collageOrientation);
            fd.append('properties[_print_pixels]', `${out.w}x${out.h}`);
            fd.append('properties[_print_cm]', `${d.w}x${d.h}`);
            fd.append('properties[_effective_ppi]', String(out.ppi));
            fd.append('properties[_quality_status]', this.quality(out.ppi));
            fd.append('properties[_collage_layout]', this.layoutId);
            fd.append('properties[_spacing]', this.spacing);
            fd.append('properties[_artwork_count]', String(this.images.length));
            fd.append('properties[_cell_ppi]', out.cellPpi.map((p, i) => `${i + 1}:${p}`).join(';'));
            fd.append('properties[_original_filename]', this.images.map((i) => i.file.name).join(' | ').slice(0, 250));
            fd.append('properties[_source_pixels]', this.images.map((i, k) => `${k + 1}:${i.width}x${i.height}`).join(';'));
            forms.push(fd);
          } else {
            for (const img of this.images) {
              const out = await this.renderSingle(img);
              const d = this.dims(img);
              const fd = new FormData();
              fd.append('id', img.variantId);
              base(fd);
              const stem = img.file.name.replace(/\.[^.]+$/, '').replace(/[^\w-]+/g, '-').slice(0, 60) || 'print';
              fd.append('properties[Print file]', out.blob, `${stem}-huemna-crop.jpg`);
              fd.append('properties[_orientation]', img.orientation);
              fd.append('properties[_print_pixels]', `${out.w}x${out.h}`);
              fd.append('properties[_print_cm]', `${d.w}x${d.h}`);
              fd.append('properties[_crop_px]', `x${out.crop.x},y${out.crop.y},w${out.crop.w},h${out.crop.h},zoom${out.crop.zoom}`);
              fd.append('properties[_effective_ppi]', String(out.ppi));
              fd.append('properties[_quality_status]', this.quality(out.ppi));
              fd.append('properties[_source_pixels]', `${img.width}x${img.height}`);
              fd.append('properties[_original_filename]', img.file.name.slice(0, 250));
              forms.push(fd);
            }
          }
          e.primary.textContent = this.dataset.addingLabel;
          for (const fd of forms) {
            const r = await fetch(`${this.dataset.cartAddUrl}.js`, { method: 'POST', body: fd, headers: { Accept: 'application/json' } });
            if (!r.ok) throw new Error(`cart ${r.status}`);
          }
          this.dispatch('custom_print_add_to_cart_intent', { product_id: this.dataset.productId, mode: this.mode, lines: forms.length, file_count: this.images.length });
          window.location.href = this.dataset.cartUrl;
        } catch (err) {
          console.error('[huemna-custom-print]', err);
          this.busy = false;
          this.setMessage(2, this.dataset.uploadError);
          this.updateSummary();
          this.updatePrimary();
        }
      }
    }
  );
})();
